/**
 * Row action overflow menu ("⋯") for desk tables: one kebab button that opens
 * an anchored menu of actions. Destructive items render in red text; the
 * caller still confirms them in a dialog.
 */
import { useRef, useState } from 'react';

import { Popover } from './popover';

export interface RowMenuItem {
  id: string;
  label: string;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  isDisabled?: boolean;
  /** Why it is disabled (shown as the item's title). */
  disabledReason?: string;
}

export interface RowMenuProps {
  items: readonly RowMenuItem[];
  /** Accessible name of the kebab, e.g. "More actions for coach@team.com". */
  label: string;
}

export function RowMenu({ items, label }: RowMenuProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  if (items.length === 0) return null;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="dt-kebab"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      <Popover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label={label}
        role="menu"
        className="rowmenu dt-rowmenu"
        width={200}
        align="end"
      >
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            className={`rowmenu__item${item.tone === 'danger' ? ' rowmenu__item--danger' : ''}`}
            disabled={item.isDisabled}
            title={item.isDisabled ? item.disabledReason : undefined}
            onClick={() => {
              setIsOpen(false);
              item.onSelect();
            }}
          >
            {item.label}
          </button>
        ))}
      </Popover>
    </>
  );
}
